import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Card, CardContent } from '@jeraisonne/ui';
import { CheckCircle2, HeartHandshake, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useAcceptParentLink } from '@/lib/parent-link';

/**
 * Cible des liens / QR de liaison parent ⇄ enfant (migration 0150).
 * Accessible à n'importe quel compte authentifié (parent OU élève) : c'est la
 * RPC `accept_parent_link` qui déduit l'orientation des rôles et refuse
 * proprement si les deux comptes sont du même type.
 */
export function AcceptParentLinkPage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const accept = useAcceptParentLink();
  const [result, setResult] = useState<{ name: string } | null>(null);

  const isParent = profile?.role === 'parent';
  const homeTo = isParent ? '/parent/children' : '/student/contacts';
  const homeLabel = isParent ? 'Voir mes enfants' : 'Voir mes contacts';

  function handleAccept() {
    accept.mutate(code, {
      onSuccess: (res) => setResult({ name: res.linked_display_name }),
    });
  }

  return (
    <div className="pb-safe mx-auto max-w-md px-4 py-6 sm:py-10">
      <Card className="border-ink-100">
        <CardContent className="space-y-5 py-6">
          {result ? (
            <>
              <div className="flex flex-col items-center text-center">
                <CheckCircle2 className="mb-2 h-12 w-12 text-emerald-500" />
                <h1 className="font-display text-ink-900 text-xl font-extrabold">
                  Comptes reliés&nbsp;!
                </h1>
                <p className="text-ink-500 mt-1 text-sm">
                  Ton compte est maintenant relié à celui de{' '}
                  <span className="text-ink-900 font-semibold">{result.name}</span>.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate({ to: homeTo })}>
                {homeLabel}
              </Button>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center">
                <div className="bg-primary-50 mb-2 flex h-14 w-14 items-center justify-center rounded-full">
                  <HeartHandshake className="text-primary-600 h-7 w-7" />
                </div>
                <h1 className="font-display text-ink-900 text-xl font-extrabold">
                  Relier vos comptes
                </h1>
                <p className="text-ink-500 mt-1 text-sm">
                  Quelqu'un t'a partagé son code de liaison parent-enfant. En l'acceptant, vos
                  comptes seront reliés et le parent pourra suivre la progression de l'élève.
                </p>
                <p className="text-ink-400 mt-2 font-mono text-xs tracking-widest">Code : {code}</p>
              </div>

              {accept.isError && (
                <p className="text-accent-600 text-center text-sm">
                  {(accept.error as Error).message}
                </p>
              )}

              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  onClick={handleAccept}
                  disabled={accept.isPending || !user}
                >
                  {accept.isPending ? 'Validation…' : 'Accepter'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={() => navigate({ to: homeTo })}
                >
                  <X className="h-4 w-4" />
                  Refuser
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
